import { Injectable, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { addHours } from 'date-fns/addHours';
import { formatDate } from 'date-fns/format';
import { toDate } from 'date-fns/toDate';
import { tap } from 'rxjs';

import {
  RoomBuildingsModel,
  RoomLocationsBaseModel,
  RoomQueryParams,
} from '@bookly/shared';

import { RoomsService } from '../../services/rooms.service';

export interface FiltersChipsModel {
  label: string;
  value: string | number;
  formControl: string;
  formGroup?: string;
}

export interface RoomFiltersKeys {
  [key: string]: FormGroup | FormControl;
}

export interface RoomFiltersFormValueModel extends RoomFiltersKeys {
  roomName: FormControl<string | null>;
  fromDate: FormControl<Date | null>;
  fromTime: FormControl<Date | null>;
  toDate: FormControl<Date | null>;
  toTime: FormControl<Date | null>;
  minCapacity: FormControl<number | null>;
  maxCapacity: FormControl<number | null>;
  roomTypes: FormGroup<FiltersCheckboxFormValueModel>;
  roomFeatures: FormGroup<FiltersCheckboxFormValueModel>;
  siteId: FormControl<number | null>;
  buildingId: FormControl<number | null>;
  floorId: FormControl<number | null>;
  bookDirectly: FormControl<boolean>;
  bookExternal: FormControl<boolean>;
}

export interface FiltersCheckboxFormValueModel {
  [key: string]: FormControl<boolean>;
}

export interface SelectedDateRangeModel {
  fromDate: Date;
  toDate: Date;
  fromTime: Date | null;
  toTime: Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class FiltersFormService {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #roomsService = inject(RoomsService);
  readonly #fb = inject(FormBuilder);
  readonly #roomFiltersData = this.#roomsService.roomFiltersData;
  readonly #activeFiltersChips = signal<FiltersChipsModel[]>([]);
  readonly queryParams: Params = this.#route.snapshot.queryParams;

  readonly #filtersForm: FormGroup<RoomFiltersFormValueModel> = this.#fb.group({
    roomName: this.#fb.control<string | null>(
      this.queryParams['roomName'] ?? null
    ),
    fromDate: this.#fb.control<Date | null>(
      this.queryParams['fromDate']
        ? toDate(
            this.queryParams['fromDate']
              .toString()
              .substring(0, this.queryParams['fromDate'].toString().length - 1)
          )
        : null
    ),
    fromTime: this.#fb.control<Date | null>(
      this.queryParams['fromDate']
        ? toDate(
            this.queryParams['fromDate']
              .toString()
              .substring(0, this.queryParams['fromDate'].toString().length - 1)
          )
        : null
    ),
    toDate: this.#fb.control<Date | null>(
      this.queryParams['toDate']
        ? toDate(
            this.queryParams['toDate']
              .toString()
              .substring(0, this.queryParams['toDate'].toString().length - 1)
          )
        : null
    ),
    toTime: this.#fb.control<Date | null>(
      this.queryParams['toDate']
        ? toDate(
            this.queryParams['toDate']
              .toString()
              .substring(0, this.queryParams['toDate'].toString().length - 1)
          )
        : null
    ),
    minCapacity: this.#fb.control<number | null>(
      this.queryParams['minCapacity'] ?? null
    ),
    maxCapacity: this.#fb.control<number | null>(
      this.queryParams['maxCapacity'] ?? null
    ),
    roomTypes: this.#fb.group({}),
    roomFeatures: this.#fb.group({}),
    siteId: this.#fb.control<number | null>(
      this.queryParams['siteId'] ? +this.queryParams['siteId'] : null
    ),
    buildingId: this.#fb.control<number | null>({
      value: this.queryParams['buildingId']
        ? +this.queryParams['buildingId']
        : null,
      disabled: !this.queryParams['siteId'],
    }),
    floorId: this.#fb.control<number | null>({
      value: this.queryParams['floorId'] ? +this.queryParams['floorId'] : null,
      disabled: !this.queryParams['buildingId'],
    }),
    bookDirectly: this.#fb.nonNullable.control<boolean>(false),
    bookExternal: this.#fb.nonNullable.control<boolean>(false),
  });
  readonly #availableOnControl = this.#fb.nonNullable.control<boolean>(false);

  #buildings = signal<RoomBuildingsModel[]>([]);
  #floors = signal<RoomLocationsBaseModel[]>([]);

  get filtersForm() {
    return this.#filtersForm;
  }

  get availableOnControl() {
    return this.#availableOnControl;
  }

  get buildings() {
    return this.#buildings.asReadonly();
  }

  get floors() {
    return this.#floors.asReadonly();
  }

  get roomFiltersData() {
    return this.#roomFiltersData;
  }

  get activeFiltersChips() {
    return this.#activeFiltersChips.asReadonly();
  }

  get siteIdValueChanges() {
    return this.#filtersForm.controls.siteId.valueChanges.pipe(
      tap(siteId => {
        const roomFiltersData = this.#roomFiltersData();
        if (roomFiltersData) {
          const buildings = roomFiltersData.locations.find(
            site => site.id === siteId
          )?.buildings;

          this.#buildings.set(buildings ?? []);
        }
        if (this.buildings().length > 0) {
          this.#filtersForm.controls.buildingId.enable();
        } else {
          this.#filtersForm.controls.buildingId.disable();
        }
        this.#filtersForm.controls.buildingId.patchValue(null, {
          emitEvent: false,
        });
        this.#filtersForm.controls.floorId.patchValue(null, {
          emitEvent: false,
        });
      })
    );
  }

  get buildingIdValueChanges() {
    return this.#filtersForm.controls.buildingId.valueChanges.pipe(
      tap(buildingId => {
        const roomFiltersData = this.#roomFiltersData();
        const buildings = this.#buildings();
        if (roomFiltersData && buildings) {
          const floors = buildings.find(
            building => building.id === buildingId
          )?.floors;
          this.#floors.set(floors ?? []);
        }
        if (this.floors().length > 0) {
          this.#filtersForm.controls.floorId.enable();
        } else {
          this.#filtersForm.controls.floorId.disable();
        }
        this.#filtersForm.controls.floorId.patchValue(null, {
          emitEvent: false,
        });
      })
    );
  }

  get fromDateValueChanges() {
    return this.#filtersForm.controls.fromDate.valueChanges.pipe(
      tap(fromDate => {
        if (!fromDate) {
          this.#filtersForm.controls.toDate.patchValue(null, {
            emitEvent: false,
          });
          return;
        }
        const currentToDate = this.#filtersForm.controls.toDate.value;
        if (!currentToDate || currentToDate.getTime() < fromDate.getTime()) {
          this.#filtersForm.controls.toDate.patchValue(fromDate, {
            emitEvent: false,
          });
        }
      })
    );
  }

  get availableOnControlValueChanges() {
    return this.#availableOnControl.valueChanges.pipe(
      tap(controlValue => {
        if (controlValue) {
          const fromDate = new Date();
          const toDate = addHours(fromDate, 1);
          this.#filtersForm.controls.fromDate.patchValue(fromDate);
          this.#filtersForm.controls.fromTime.patchValue(fromDate);
          this.#filtersForm.controls.toDate.patchValue(toDate);
          this.#filtersForm.controls.toTime.patchValue(toDate);
        } else {
          this.#filtersForm.controls.fromDate.patchValue(null);
          this.#filtersForm.controls.fromTime.patchValue(null);
          this.#filtersForm.controls.toDate.patchValue(null);
          this.#filtersForm.controls.toTime.patchValue(null);
        }
      })
    );
  }

  public populateRoomFilters() {
    const roomFiltersData = this.#roomFiltersData();

    if (roomFiltersData) {
      roomFiltersData.roomTypes.forEach(roomType => {
        const typeControl = this.#fb.nonNullable.control(false);
        this.#filtersForm.controls.roomTypes.addControl(
          roomType.name,
          typeControl,
          { emitEvent: false }
        );
      });
      roomFiltersData.roomFeatures.roomMustInclude.forEach(roomFeature => {
        const featureControl = this.#fb.nonNullable.control(false);
        this.#filtersForm.controls.roomFeatures.addControl(
          roomFeature.id.toString(),
          featureControl,
          { emitEvent: false }
        );
      });
      if (this.queryParams['roomTypes']) {
        const roomTypes = this.queryParams['roomTypes'].split(',');
        roomTypes.forEach((roomType: string) => {
          this.#filtersForm.controls.roomTypes.controls[roomType].patchValue(
            true,
            { emitEvent: false }
          );
        });
      }

      if (this.queryParams['featureIds']) {
        const featureIds = this.queryParams['featureIds'].split(',');
        featureIds.forEach((featureId: string) => {
          this.#filtersForm.controls.roomFeatures.controls[
            featureId
          ].patchValue(true, { emitEvent: false });
        });
      }

      if (this.queryParams['siteId']) {
        const buildings = roomFiltersData.locations.find(
          site => site.id === +this.queryParams['siteId']
        )?.buildings;

        this.#buildings.set(buildings ?? []);
      }
      if (this.queryParams['buildingId']) {
        const floors = this.buildings().find(
          building => building.id === +this.queryParams['buildingId']
        )?.floors;
        this.#floors.set(floors ?? []);
      }
    }
  }

  public getSelectedFormData(syncQueryParams = true): RoomQueryParams {
    const filtersChips: FiltersChipsModel[] = [];
    const selectedFilters: RoomQueryParams = {};
    const formValue = this.#filtersForm.value;
    const roomTypes = formValue.roomTypes;
    const roomFeatures = formValue.roomFeatures;
    const selectedRoomTypes: string[] = [];
    const selectedRoomFeaturesIds: number[] = [];

    if (formValue.roomName) {
      selectedFilters['roomName'] = formValue.roomName;
      filtersChips.push({
        label: 'Name',
        value: formValue.roomName,
        formControl: 'roomName',
      });
    }

    if (formValue.siteId) {
      selectedFilters['siteId'] = formValue.siteId;
      filtersChips.push({
        label: 'City',
        value:
          this.#roomFiltersData()?.locations.find(
            location => location.id === formValue.siteId
          )?.name ?? '',
        formControl: 'siteId',
      });
    }

    if (formValue.buildingId) {
      selectedFilters['buildingId'] = formValue.buildingId;
      filtersChips.push({
        label: 'Building',
        value:
          this.#buildings()?.find(
            building => building.id === formValue.buildingId
          )?.name ?? '',
        formControl: 'buildingId',
      });
    }

    if (formValue.floorId) {
      selectedFilters['floorId'] = formValue.floorId;
      filtersChips.push({
        label: 'Floor',
        value:
          this.#floors()?.find(floor => floor.id === formValue.floorId)?.name ??
          '',
        formControl: 'floorId',
      });
    }

    if (formValue.minCapacity) {
      selectedFilters['minCapacity'] = formValue.minCapacity;
      filtersChips.push({
        label: 'Min. capacity',
        value: formValue.minCapacity,
        formControl: 'minCapacity',
      });
    }

    if (formValue.maxCapacity) {
      selectedFilters['maxCapacity'] = formValue.maxCapacity;
      filtersChips.push({
        label: 'Max. capacity',
        value: formValue.maxCapacity,
        formControl: 'maxCapacity',
      });
    }

    if (formValue.fromDate) {
      // ToDo check if date filter is behaving correctly, since the Z is added in post not during formating (we are adding z to local date)
      const timeFrom = formValue.fromTime
        ? formatDate(formValue.fromTime, 'HH:mm:00')
        : '00:00:00';
      const dateString = formatDate(
        formValue.fromDate,
        "yyyy-MM-dd'T'" + timeFrom
      );
      selectedFilters['fromDate'] = dateString + 'Z';
      filtersChips.push({
        label: 'Date from',
        value: formatDate(new Date(dateString), 'dd/MM/yyy HH:mm'),
        formControl: 'fromDate',
      });
    }

    if (formValue.toDate) {
      const timeFrom = formValue.toTime
        ? formatDate(formValue.toTime, 'HH:mm:59')
        : '23:59:59';
      const dateString = formatDate(
        formValue.toDate,
        "yyyy-MM-dd'T'" + timeFrom
      );
      selectedFilters['toDate'] = dateString + 'Z';
      filtersChips.push({
        label: 'Date to',
        value: formatDate(new Date(dateString), 'dd/MM/yyy HH:mm'),
        formControl: 'toDate',
      });
    }

    if (roomTypes) {
      Object.entries(roomTypes).forEach(([key, value]) => {
        if (value) {
          selectedRoomTypes.push(key);
          filtersChips.push({
            label: 'Room type',
            value: key,
            formControl: key,
            formGroup: 'roomTypes',
          });
        }
      });
    }

    if (roomFeatures) {
      Object.entries(roomFeatures).forEach(([key, value]) => {
        if (value) {
          selectedRoomFeaturesIds.push(+key);
          const roomMustIncludeItem =
            this.#roomFiltersData()?.roomFeatures.roomMustInclude.find(
              feature => feature.id === +key
            );
          const roomTypeSetupItem =
            this.#roomFiltersData()?.roomFeatures.roomTypeSetup.find(
              feature => feature.id === +key
            );

          if (roomTypeSetupItem) {
            filtersChips.push({
              label: 'Room setup',
              value: roomTypeSetupItem.name,
              formControl: key,
              formGroup: 'roomFeatures',
            });
          }

          if (roomMustIncludeItem) {
            filtersChips.push({
              label: 'Room includes',
              value: roomMustIncludeItem.name,
              formControl: key,
              formGroup: 'roomFeatures',
            });
          }
        }
      });
    }

    if (selectedRoomTypes.length > 0) {
      selectedFilters['roomTypes'] = selectedRoomTypes;
    }

    if (selectedRoomFeaturesIds.length > 0) {
      selectedFilters['featureIds'] = selectedRoomFeaturesIds;
    }

    const bookDirectly = formValue.bookDirectly;
    const bookExternal = formValue.bookExternal;
    if (bookDirectly && !bookExternal) {
      selectedFilters['isManageSpace'] = true;
    } else if (!bookDirectly && bookExternal) {
      selectedFilters['isManageSpace'] = false;
    }

    if (bookDirectly) {
      filtersChips.push({
        label: 'Booking',
        value: 'On Request',
        formControl: 'bookDirectly',
      });
    }

    if (bookExternal) {
      filtersChips.push({
        label: 'Booking',
        value: 'Instant Booking',
        formControl: 'bookExternal',
      });
    }

    this.#activeFiltersChips.set(filtersChips);

    if (syncQueryParams) {
      void this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: this.#buildRouterQueryParams(selectedFilters),
        queryParamsHandling: 'replace',
        skipLocationChange: false,
        info: { skipViewTransition: true },
      });
    }

    return selectedFilters;
  }

  public getSelectedDateRange(): SelectedDateRangeModel | null {
    const fromDateControlValue = this.#filtersForm.controls.fromDate.value;
    const toDateControlValue = this.#filtersForm.controls.toDate.value;

    if (!fromDateControlValue && !toDateControlValue) {
      return null;
    }

    const fromDateValue = fromDateControlValue ?? toDateControlValue;
    const toDateValue = toDateControlValue ?? fromDateControlValue;

    if (!fromDateValue || !toDateValue) {
      return null;
    }

    const fromTimeValue = this.#filtersForm.controls.fromTime.value;
    const toTimeValue = this.#filtersForm.controls.toTime.value;

    const fromDate = new Date(fromDateValue);
    const toDate = new Date(toDateValue);

    const fromDateForRange = new Date(fromDate);
    const toDateForRange = new Date(toDate);

    if (fromTimeValue) {
      fromDateForRange.setHours(
        fromTimeValue.getHours(),
        fromTimeValue.getMinutes(),
        0,
        0
      );
    } else {
      fromDateForRange.setHours(0, 0, 0, 0);
    }

    if (toTimeValue) {
      toDateForRange.setHours(
        toTimeValue.getHours(),
        toTimeValue.getMinutes(),
        59,
        0
      );
    } else {
      toDateForRange.setHours(23, 59, 59, 0);
    }

    if (fromDateForRange.getTime() >= toDateForRange.getTime()) {
      return null;
    }

    return {
      fromDate,
      toDate,
      fromTime: fromTimeValue,
      toTime: toTimeValue,
    };
  }

  public resetForm() {
    this.#filtersForm.reset();
    this.#availableOnControl.reset();
  }

  public resetFormControl(formControlName: string, formGroup?: string) {
    if (
      formGroup &&
      this.#filtersForm.controls[formGroup] instanceof FormGroup
    ) {
      this.#filtersForm.controls[formGroup].controls[formControlName].reset();
    } else {
      this.#filtersForm.controls[formControlName].reset();
      if (formControlName === 'fromDate') {
        this.#filtersForm.controls['fromTime'].reset();
      }
      if (formControlName === 'toDate') {
        this.#filtersForm.controls['toTime'].reset();
      }
    }

    this.getSelectedFormData();
  }

  #buildRouterQueryParams(params: RoomQueryParams): Params {
    // On the favorites route the calendar always shows favorites only, so
    // filters are not reflected in the URL — keep just the calendar view-state
    // param.
    if (this.#isFavoriteRoute()) {
      const calendarView = this.#route.snapshot.queryParams['calendarView'];
      return calendarView ? { calendarView } : {};
    }
    const queryParams: Params = { ...params };
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value instanceof Array) {
        queryParams[key] = value.join(',');
      }
    });
    // Preserve view-state params that aren't filters so they survive the
    // `queryParamsHandling: 'replace'` navigation (e.g. the calendar tab shortcut).
    const calendarView = this.#route.snapshot.queryParams['calendarView'];
    if (calendarView) {
      queryParams['calendarView'] = calendarView;
    }
    return queryParams;
  }

  /** Whether the user is on the favorites route (`/dashboard/favorites`). */
  #isFavoriteRoute(): boolean {
    return (
      this.#router.url?.split('?')[0].split('/').includes('favorites') ?? false
    );
  }
}
